/* Paper Party Studio theme scripts (styles in paper-party.css) */
(function () {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  // Logo order: Party Pink, Coral Red, Marigold, Sunshine, Leaf Green, Sky Teal, Denim Blue
  const RAINBOW = ['party-pink', 'candy-red', 'marigold', 'sunshine', 'mint', 'sky', 'grape'];

  /* ---------- Bunting ----------
     Real bunting proportions: small flags a little taller than wide, hung in
     short gentle swags (not one long sag across the screen), each flag
     tilting with the string where it hangs. Sizes come from the strip's
     height, drawn at real pixels so nothing is ever stretched; wider
     screens simply get more swags. */
  function drawBunting(svg) {
    const width = Math.round(svg.clientWidth);
    const height = Math.round(svg.clientHeight);
    if (!width || !height) return;
    if (svg.dataset.drawnWidth === String(width) && svg.dataset.drawnHeight === String(height)) return;

    const top = 3; // where the string is pinned
    const sag = Math.max(5, Math.round(height * 0.2)); // dip in the middle of each swag
    const flagHeight = height - top - sag - 4; // leave room for the stroke at the tip
    const flagWidth = flagHeight / 1.2; // a touch taller than wide
    const gap = flagWidth * 0.22;
    const perSwag = width < 750 ? 4 : 5;

    // Whole swags only, so both ends are pinned and every swag matches
    const idealSwag = perSwag * (flagWidth + gap);
    const swags = Math.max(1, Math.round(width / idealSwag));
    const swag = width / swags;
    const slot = swag / perSwag;
    const w = Math.min(flagWidth, slot - 3);

    // Quadratic swag: y along the string, and its slope, at any x
    const local = (x) => ((x % swag) + swag) % swag;
    const yAt = (x) => {
      const t = local(x) / swag;
      return top + 4 * sag * t * (1 - t);
    };
    const slopeAt = (x) => (4 * sag * (1 - (2 * local(x)) / swag)) / swag;

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svg.replaceChildren();

    let d = `M0 ${top}`;
    for (let k = 0; k < swags; k++) {
      d += ` Q ${(k + 0.5) * swag} ${top + 2 * sag} ${(k + 1) * swag} ${top}`;
    }
    const string = document.createElementNS(SVG_NS, 'path');
    string.setAttribute('class', 'string');
    string.setAttribute('d', d);
    svg.append(string);

    let index = 0;
    for (let k = 0; k < swags; k++) {
      for (let j = 0; j < perSwag; j++) {
        const cx = k * swag + (j + 0.5) * slot;
        const cy = yAt(cx);
        // Flag hangs square to the string: its top edge follows the tangent
        const angle = Math.atan(slopeAt(cx));
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const half = w / 2;
        const p1 = [cx - half * cos, cy - half * sin];
        const p2 = [cx + half * cos, cy + half * sin];
        const tip = [cx - flagHeight * sin, cy + flagHeight * cos];

        const flag = document.createElementNS(SVG_NS, 'polygon');
        flag.setAttribute('points', [p1, p2, tip].map((pt) => pt.map((n) => n.toFixed(1)).join(',')).join(' '));
        flag.style.fill = `var(--${RAINBOW[index % RAINBOW.length]})`;
        flag.style.setProperty('--i', String(index));
        svg.append(flag);
        index++;
      }
    }

    // Flags unfurl on the first draw only, not on every resize
    svg.classList.toggle('is-first', !svg.dataset.drawnWidth);
    svg.dataset.drawnWidth = String(width);
    svg.dataset.drawnHeight = String(height);
  }

  const buntingObserver =
    'ResizeObserver' in window
      ? new ResizeObserver((entries) => entries.forEach((entry) => drawBunting(entry.target)))
      : null;

  function initBunting(root) {
    root.querySelectorAll('svg.pp-bunting').forEach((svg) => {
      drawBunting(svg);
      if (buntingObserver) buntingObserver.observe(svg);
    });
  }

  /* ---------- Quick add vs Personalise on product cards ----------
     Cards marked data-pp-auto carry both buttons. The Easify Product Options
     app lists which products have personalisation fields; those show
     "Personalise" (the fields live on the product page), the rest quick add.
     If Easify's data is missing or uses rules we can't read, the Liquid
     fallback stays in place. */
  function easifyProductIds() {
    const sets = window.TPOConfigs && window.TPOConfigs.option_sets_searchable;
    if (!Array.isArray(sets)) return null;
    const ids = new Set();
    for (const set of sets) {
      const conditions = set.conditions || {};
      const active = !set.data || Number(set.data.status) === 1;
      if (!active) continue;
      // apply_type 1 = hand-picked products; anything else is rule based
      if (Number(conditions.apply_type) !== 1) return null;
      (conditions.option_set_products || []).forEach((item) => {
        ids.add(String(item.product_id).split('/').pop());
      });
    }
    return ids;
  }

  function applyCardModes(root) {
    const ids = easifyProductIds();
    if (!ids) return;
    root.querySelectorAll('.pp-card-actions[data-pp-auto]').forEach((el) => {
      el.dataset.ppMode = ids.has(el.dataset.productId) ? 'personalise' : 'quick';
    });
  }

  // Cards added later (recommendations, infinite scroll, quick views)
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && (node.matches('.pp-card-actions') || node.querySelector('.pp-card-actions'))) {
            applyCardModes(node.parentElement || document);
            applyCartState(node.parentElement || document);
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---------- Product description as collapsible sections ----------
     blocks/pp-description.liquid renders the description as-is; this
     regroups it: the opening paragraph stays visible, each heading (h2-h4)
     and each paragraph that starts with a bold label ("Perfect for ·...")
     becomes a collapsible section, and a closing italic note stays visible. */
  const isBlankText = (node) => node.nodeType === 3 && !node.textContent.trim();

  function leadingStrong(el) {
    if (el.tagName !== 'P') return null;
    const first = [...el.childNodes].find((node) => !isBlankText(node));
    return first && first.nodeName === 'STRONG' ? first : null;
  }

  function isClosingNote(el, index, total) {
    return (
      index === total - 1 &&
      el.tagName === 'P' &&
      el.textContent.trim() !== '' &&
      [...el.childNodes].every((node) => isBlankText(node) || node.nodeName === 'EM')
    );
  }

  // Remove the label and any leading line break / "·" so only the content remains
  function labelBody(paragraph, strong) {
    const body = paragraph.cloneNode(true);
    const index = [...paragraph.childNodes].indexOf(strong);
    body.removeChild(body.childNodes[index]);
    while (body.firstChild) {
      const node = body.firstChild;
      if (node.nodeName === 'BR' || isBlankText(node)) {
        node.remove();
      } else if (node.nodeType === 3) {
        node.textContent = node.textContent.replace(/^[\s·:\-–]+/, '');
        break;
      } else {
        break;
      }
    }
    return body;
  }

  function asTags(body) {
    if (body.querySelector('br, a')) return null;
    const parts = body.textContent.split('·').map((part) => part.trim()).filter(Boolean);
    if (parts.length < 2 || parts.some((part) => part.length > 60)) return null;
    const list = document.createElement('ul');
    list.className = 'pp-description__tags';
    parts.forEach((part) => {
      const item = document.createElement('li');
      item.textContent = part.replace(/\.$/, '');
      list.append(item);
    });
    return list;
  }

  const normaliseTitle = (text) => text.toLowerCase().replace(/[’‘]/g, "'").trim();
  const KNOWN_SECTIONS = [
    "the story",
    "why you'll love it",
    'make it yours',
    'how to order',
    'perfect for',
    "what's in the box",
    'care',
    'size',
    'delivery',
    'shipping',
  ];

  function buildDescription(root) {
    if (root.dataset.ppReady) return;
    const source = root.querySelector('.pp-description__source');
    if (!source) return;
    const elements = [...source.children];
    const intro = [];
    const sections = [];
    let note = null;
    let current = null;
    // A label on its own line ("What's in the box") gathers the labelled lines
    // under it ("Classic Set · ...", "Grand Set · ...") until the next known section
    let gathering = false;

    elements.forEach((el, index) => {
      if (isClosingNote(el, index, elements.length)) {
        note = el;
        return;
      }
      if (/^H[2-4]$/.test(el.tagName)) {
        current = { title: el.textContent.trim(), nodes: [] };
        sections.push(current);
        gathering = false;
        return;
      }
      const strong = leadingStrong(el);
      if (strong) {
        const title = strong.textContent.trim().replace(/[\s:·]+$/, '');
        if (gathering && !KNOWN_SECTIONS.some((known) => normaliseTitle(title).startsWith(known))) {
          current.nodes.push(el);
          return;
        }
        current = { title, nodes: [] };
        sections.push(current);
        const body = labelBody(el, strong);
        gathering = !body.textContent.trim();
        if (!gathering) current.nodes.push(asTags(body) || body);
        return;
      }
      (current ? current.nodes : intro).push(el);
    });

    // Nothing to fold (a short description): leave it exactly as written
    if (sections.length === 0) return;

    const openPrefix = normaliseTitle(root.dataset.open || '');
    const shortenStory = root.dataset.shortenStory === 'true';

    const introEl = document.createElement('div');
    introEl.className = 'pp-description__intro rte';
    intro.forEach((node) => introEl.append(node));

    // "Perfect for" is short, so its tags stay visible instead of folding away
    let perfect = null;
    const folded = sections.filter((section) => {
      const tags = section.nodes.length === 1 && section.nodes[0].classList && section.nodes[0].classList.contains('pp-description__tags');
      if (!perfect && tags && normaliseTitle(section.title).startsWith('perfect for')) {
        perfect = document.createElement('div');
        perfect.className = 'pp-description__perfect';
        const label = document.createElement('p');
        label.className = 'pp-description__perfect-label';
        label.textContent = section.title;
        perfect.append(label, section.nodes[0]);
        return false;
      }
      return true;
    });

    // One line of what's inside, shown under a closed row's title
    const previewOf = (nodes) => {
      const text = nodes
        .map((node) =>
          node.tagName === 'UL' || node.tagName === 'OL'
            ? [...node.children].map((item) => item.textContent.trim()).join(', ')
            : node.textContent
        )
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      return text.length > 72 ? `${text.slice(0, 70).replace(/[\s,.;:·-]+$/, '')}…` : text;
    };

    const list = document.createElement('div');
    list.className = 'pp-description__sections';
    let opened = false;
    folded.forEach((section) => {
      let title = section.title;
      if (shortenStory && /^the story behind\b/i.test(title)) title = 'The story';

      const details = document.createElement('details');
      details.className = 'pp-accordion';
      if (!opened && openPrefix && normaliseTitle(title).startsWith(openPrefix)) {
        details.open = true;
        opened = true;
      }
      const summary = document.createElement('summary');
      summary.className = 'pp-accordion__summary';
      const text = document.createElement('span');
      text.className = 'pp-accordion__text';
      const label = document.createElement('span');
      label.className = 'pp-accordion__title';
      label.textContent = title;
      text.append(label);
      const preview = previewOf(section.nodes);
      if (preview) {
        const line = document.createElement('span');
        line.className = 'pp-accordion__preview';
        line.textContent = preview;
        text.append(line);
      }
      const icon = document.createElement('span');
      icon.className = 'pp-accordion__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<svg viewBox="0 0 20 20"><path d="M5 7.5l5 5 5-5"/></svg>';
      summary.append(text, icon);

      const body = document.createElement('div');
      body.className = 'pp-accordion__body rte';
      section.nodes.forEach((node) => body.append(node));

      details.append(summary, body);
      list.append(details);
    });

    source.replaceChildren();
    if (introEl.childNodes.length) source.append(introEl);
    if (perfect) source.append(perfect);
    if (list.childNodes.length) source.append(list);
    if (note) {
      note.classList.add('pp-description__note');
      source.append(note);
    }
    source.classList.remove('rte');
    root.dataset.ppReady = 'true';
  }

  function initDescriptions(root) {
    root.querySelectorAll('[data-pp-description]').forEach(buildDescription);
  }

  /* ---------- Sticky checkout bar on the cart page (phones) ----------
     Shows the total and a Check out button (it submits the same cart form)
     whenever the page's own Check out button is scrolled out of view. */
  function initCartBar() {
    const checkout = document.querySelector('main .cart__checkout-button[form]');
    if (!checkout || document.querySelector('.pp-cart-bar')) return;

    const bar = document.createElement('div');
    bar.className = 'pp-cart-bar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Cart total and checkout');
    bar.innerHTML =
      '<div class="pp-cart-bar__total"><span class="pp-cart-bar__label">Total</span>' +
      '<span class="pp-cart-bar__amount"></span></div>';
    const button = document.createElement('button');
    button.type = 'submit';
    button.name = 'checkout';
    button.className = 'button cart__checkout-button';
    button.setAttribute('form', checkout.getAttribute('form'));
    button.textContent = checkout.textContent.trim() || 'Check out';
    bar.append(button);
    document.body.append(bar);

    const amount = bar.querySelector('.pp-cart-bar__amount');
    let target = null;
    const io =
      'IntersectionObserver' in window
        ? new IntersectionObserver((entries) => {
            const onScreen = entries.some((entry) => entry.isIntersecting);
            bar.classList.toggle('is-visible', !onScreen);
          })
        : null;

    const sync = () => {
      const current = document.querySelector('main .cart__checkout-button[form]');
      // An emptied cart has no checkout: remove the bar
      if (!current) {
        bar.remove();
        return;
      }
      const total = document.querySelector('main .cart-totals__total-value');
      amount.textContent = total ? total.textContent.trim() : '';
      // Quantity changes re-render the cart; follow the new Check out button
      if (io && current !== target) {
        if (target) io.unobserve(target);
        target = current;
        io.observe(target);
      }
    };
    sync();
    new MutationObserver(sync).observe(document.querySelector('main'), {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /* ---------- Motion ----------
     Styles in paper-party.css (.pp-motion); all of it is skipped when the
     visitor asks for reduced motion. */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Scroll reveal: cards and section titles rise in as they come into view,
  // staggered within their row. In carousels they appear as you swipe.
  const REVEAL_SELECTORS = [
    '.product-grid > .product-grid__item',
    '.resource-list__item',
    '.pp-features__item',
    '.shopify-section:has(.resource-list) .text-block :is(h1, h2, h3, h4)',
    '.pp-features__header',
    '.pp-hero__visual',
  ].join(',');

  const revealObserver =
    !reduceMotion && 'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              observer.unobserve(entry.target);
            });
          },
          { threshold: 0.12, rootMargin: '0px 0px -24px 0px' }
        )
      : null;

  function initReveal(root) {
    if (!revealObserver) return;
    document.documentElement.classList.add('pp-motion');
    root.querySelectorAll(REVEAL_SELECTORS).forEach((el) => {
      if (el.hasAttribute('data-pp-reveal')) return;
      // Already on screen at load: leave it be, hiding it would flicker
      const rect = el.getBoundingClientRect();
      const onScreen =
        rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
      if (onScreen && document.readyState !== 'complete') {
        el.setAttribute('data-pp-reveal', '');
        el.classList.add('is-in');
        return;
      }
      const siblings = el.parentElement ? [...el.parentElement.children] : [el];
      const index = Math.min(siblings.indexOf(el), 5);
      el.style.setProperty('--pp-delay', `${Math.max(index, 0) * 70}ms`);
      el.setAttribute('data-pp-reveal', '');
      revealObserver.observe(el);
    });
  }

  // Add to cart: a small confetti burst from the button that was tapped,
  // and a bounce on the cart count. The button is remembered on tap and the
  // burst plays only if the cart update that follows succeeds.
  let lastAddButton = null;
  let lastAddAt = 0;
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('.add-to-cart-button, .quick-add__button');
      if (!button) return;
      lastAddButton = button;
      lastAddAt = Date.now();
    },
    true
  );

  function confettiBurst(from, container = document.body) {
    const rect = from.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const colours = RAINBOW;
    for (let i = 0; i < 14; i++) {
      const dot = document.createElement('span');
      dot.className = 'pp-confetti';
      dot.style.left = `${x - 3.5}px`;
      dot.style.top = `${y - 3.5}px`;
      dot.style.background = `var(--${colours[i % colours.length]})`;
      container.append(dot);
      // Safety net: animations pause in background tabs, so never leave dots behind
      setTimeout(() => dot.remove(), 1400);
      const angle = (Math.PI * 2 * i) / 14 + Math.random() * 0.4;
      const distance = 36 + Math.random() * 28;
      dot
        .animate(
          [
            { transform: 'translate(0, 0) scale(1)', opacity: 1 },
            {
              transform: `translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance - 12}px) scale(.4)`,
              opacity: 0,
            },
          ],
          { duration: 650 + Math.random() * 200, easing: 'cubic-bezier(.2,.7,.3,1)' }
        )
        .finished.then(() => dot.remove(), () => dot.remove());
    }
  }

  document.addEventListener('cart:update', (event) => {
    const data = (event.detail && event.detail.data) || {};
    if (data.didError) return;
    const recent = lastAddButton && Date.now() - lastAddAt < 6000;
    if (!recent || reduceMotion) return;
    const button = lastAddButton;
    lastAddButton = null;

    // The cart drawer opens after an add and covers the page, so celebrate
    // inside it (from the new item) when it is open; otherwise from the button.
    // The short wait lets the drawer open and the cart count re-render.
    setTimeout(() => {
      const drawer = document.querySelector('dialog.cart-drawer__dialog[open]');
      if (drawer) {
        const row = drawer.querySelector('.cart-items__table-row .cart-items__media, .cart-drawer__heading, h2');
        if (row) confettiBurst(row, drawer);
      } else if (button.isConnected) {
        confettiBurst(button);
      }
      document.querySelectorAll('.cart-bubble').forEach((bubble) => {
        bubble.classList.remove('pp-bump');
        void bubble.offsetWidth; // restart the animation
        bubble.classList.add('pp-bump');
      });
    }, 450);
  });

  // Phones: the first carousel on a page nudges once per visit, to show it
  // can be swiped. Moves the track, not the scroll, so nothing snaps.
  function initSwipeHint() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    try {
      if (sessionStorage.getItem('pp-swipe-hint')) return;
    } catch (error) {
      /* storage unavailable: still show the hint once on this page */
    }
    const track = document.querySelector('.resource-list__carousel slideshow-slides');
    if (!track) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.intersectionRatio > 0.6)) return;
        observer.disconnect();
        setTimeout(() => {
          track.animate(
            [{ translate: '0 0' }, { translate: '-36px 0' }, { translate: '0 0' }],
            { duration: 900, easing: 'cubic-bezier(.45,0,.2,1)' }
          );
        }, 400);
        try {
          sessionStorage.setItem('pp-swipe-hint', '1');
        } catch (error) {
          /* ignore */
        }
      },
      { threshold: [0.6] }
    );
    observer.observe(track);
  }

  // Cards added later (recommendations, infinite scroll) reveal too
  if (revealObserver && 'MutationObserver' in window) {
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        initReveal(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- "In bag" state on product cards and the product page ----------
     Cards keep their bag icon and add a small "✓ 1 in bag" tag on the photo;
     the product page swaps quantity + Add to cart for a "− 1 in bag +"
     stepper that changes the cart in place. */
  let cartState = null;
  let cartFetch = null;

  // One place for the wording used on cards and the product page
  const inCartLabel = (quantity) => `${quantity} in bag`;

  function refreshCartState() {
    if (cartFetch) return cartFetch;
    cartFetch = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        cartState = cart;
        applyCartState(document);
      })
      .catch(() => {})
      .finally(() => {
        cartFetch = null;
      });
    return cartFetch;
  }

  function cartCounts() {
    const byProduct = new Map();
    const linesByVariant = new Map();
    (cartState ? cartState.items : []).forEach((item) => {
      const productId = String(item.product_id);
      const variantId = String(item.variant_id);
      byProduct.set(productId, (byProduct.get(productId) || 0) + item.quantity);
      if (!linesByVariant.has(variantId)) linesByVariant.set(variantId, []);
      linesByVariant.get(variantId).push(item);
    });
    return { byProduct, linesByVariant };
  }

  function buildTag(actions) {
    const tag = document.createElement('span');
    tag.className = 'pp-card-incart';
    actions.append(tag);
    return tag;
  }

  function applyCartState(root) {
    if (!cartState) return;
    const { byProduct } = cartCounts();
    root.querySelectorAll('.pp-card-actions[data-product-id]').forEach((actions) => {
      const quantity = byProduct.get(actions.dataset.productId) || 0;
      actions.dataset.inCart = quantity > 0 ? 'true' : 'false';
      const tag = actions.querySelector('.pp-card-incart') || (quantity > 0 ? buildTag(actions) : null);
      if (tag) tag.textContent = `✓ ${inCartLabel(quantity)}`;
    });
    applyProductPageState();
  }

  // Change one cart line, then tell the header count and cart drawer.
  // Adding ("+") opens the drawer like any other add; removing ("−") is quiet.
  async function changeLine(line, quantity, step) {
    {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity }),
      });
      if (!response.ok) throw new Error('cart change failed');
      cartState = await response.json();
      applyCartState(document);

      // Tell the header count and cart drawer. Adding one more ("+") opens
      // the drawer like any other add; removing one ("−") just updates quietly.
      const drawer = step < 0 ? document.querySelector('cart-drawer-component[auto-open]') : null;
      if (drawer) drawer.removeAttribute('auto-open');
      const update = new Event('cart:update', { bubbles: true });
      update.detail = {
        resource: cartState,
        sourceId: 'pp-stepper',
        data: { itemCount: cartState.item_count, source: 'pp-stepper' },
      };
      document.dispatchEvent(update);
      if (drawer) drawer.setAttribute('auto-open', '');
      if (step > 0) {
        document.querySelectorAll('.cart-bubble').forEach((bubble) => {
          bubble.classList.remove('pp-bump');
          void bubble.offsetWidth;
          bubble.classList.add('pp-bump');
        });
      }
    }
  }

  /* Product page: when the selected variant is already in the cart (as a
     plain line, no personalisation), the quantity box and Add to cart turn
     into the same stepper. Personalised lines keep Add to cart (a second
     one can carry different details) and show a note instead. */
  function applyProductPageState() {
    const form = document.querySelector('.product-details product-form-component');
    if (!form || !cartState) return;
    const buttons = form.querySelector('.product-form-buttons');
    const variantInput = form.querySelector('input[name="id"]');
    if (!buttons || !variantInput) return;

    const lines = cartState.items.filter((item) => String(item.variant_id) === String(variantInput.value));
    const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
    const plainLine = lines.length === 1 && !Object.keys(lines[0].properties || {}).length ? lines[0] : null;

    let stepper = buttons.querySelector('.pp-pdp-stepper');
    if (!stepper) {
      stepper = document.createElement('div');
      stepper.className = 'pp-pdp-stepper';
      stepper.innerHTML =
        '<div class="pp-pdp-stepper__pill" role="group" aria-label="Quantity in bag">' +
        '<button type="button" class="pp-pdp-stepper__btn" data-step="-1" aria-label="Remove one">&minus;</button>' +
        '<span class="pp-pdp-stepper__count" aria-live="polite"></span>' +
        '<button type="button" class="pp-pdp-stepper__btn" data-step="1" aria-label="Add one more">+</button>' +
        '</div>';
      stepper.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-step]');
        if (!button || stepper.dataset.busy === 'true') return;
        event.preventDefault();
        const current = (cartCounts().linesByVariant.get(String(variantInput.value)) || [])[0];
        if (!current) return;
        const step = Number(button.dataset.step);
        const next = Math.max(0, current.quantity + step);
        stepper.dataset.busy = 'true';
        stepper.querySelector('.pp-pdp-stepper__count').textContent = inCartLabel(next);
        try {
          await changeLine(current, next, step);
        } catch (error) {
          refreshCartState();
        } finally {
          stepper.dataset.busy = 'false';
        }
      });
      buttons.prepend(stepper);
    }

    let note = form.querySelector('.pp-pdp-incart-note');
    if (!note) {
      note = document.createElement('p');
      note.className = 'pp-pdp-incart-note';
      buttons.after(note);
    }

    const showStepper = Boolean(plainLine);
    buttons.dataset.ppInCart = showStepper ? 'true' : 'false';
    stepper.querySelector('.pp-pdp-stepper__count').textContent = inCartLabel(quantity);
    note.hidden = !(quantity > 0 && !showStepper);
    note.textContent = `✓ ${inCartLabel(quantity)}`;
  }

  // Switching variants on the product page re-checks the cart for that variant
  document.addEventListener('variant:update', () => requestAnimationFrame(applyProductPageState));

  // Keep cards in step with adds and changes made anywhere else
  document.addEventListener('cart:update', (event) => {
    const source = event.detail && event.detail.data && event.detail.data.source;
    if (source === 'pp-stepper') return;
    refreshCartState();
  });

  /* ---------- Cart drawer: return shoppers to where they were ----------
     On phones Horizon adds a history entry when the drawer opens (so the
     back button closes it) and calls history.back() on close. While open,
     the page is pinned with position:fixed, so the browser remembers a
     scroll of 0 and restores that on back, jumping to the top. Hold the
     browser's own restoration off while the drawer is open and put the
     shopper back at their spot after it closes. */
  function guardDrawerScroll() {
    const dialog = document.querySelector('dialog.cart-drawer__dialog');
    if (!dialog || dialog.dataset.ppScrollGuard) return;
    dialog.dataset.ppScrollGuard = 'true';
    let savedY = null;

    new MutationObserver(() => {
      if (dialog.open) {
        const pinnedTop = parseFloat(document.body.style.top || '0');
        savedY = pinnedTop ? -pinnedTop : window.scrollY;
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        return;
      }
      if (savedY === null) return;
      const y = savedY;
      savedY = null;
      const restore = () => window.scrollTo({ top: y, behavior: 'instant' });
      restore();
      window.addEventListener('popstate', () => requestAnimationFrame(restore), { once: true });
      setTimeout(() => {
        restore();
        if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
      }, 600);
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  /* ---------- "Buy it now" reads "Buy now" ----------
     Shopify draws this button itself (after the page loads, and again when
     the variant changes), so the label is swapped each time it appears. */
  const BUY_NOW_LABEL = 'Buy now';
  function relabelBuyNow(root) {
    root.querySelectorAll('.shopify-payment-button__button--unbranded').forEach((button) => {
      if (button.textContent.trim() !== BUY_NOW_LABEL) button.textContent = BUY_NOW_LABEL;
    });
  }
  function initBuyNow(root) {
    root.querySelectorAll('.shopify-payment-button').forEach((container) => {
      relabelBuyNow(container);
      if (container.dataset.ppBuyNow || !('MutationObserver' in window)) return;
      container.dataset.ppBuyNow = 'true';
      new MutationObserver(() => relabelBuyNow(container)).observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    });
  }

  function init(root = document) {
    initBunting(root);
    applyCardModes(root);
    initDescriptions(root);
    initBuyNow(root);
    initCartBar();
    initReveal(root);
    if (root === document) {
      initSwipeHint();
      refreshCartState();
      guardDrawerScroll();
    } else {
      applyCartState(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
